import { useAuth } from "@/context/AuthContext";
import { AppLayout } from "@/components/AppLayout";
import { Copy, Share2, MessageCircle, Users2 } from "lucide-react";
import { toast } from "sonner";
import { useEffect, useState } from "react";
import api from "@/lib/api";

export default function Referrals() {
  const { user } = useAuth();
  const [stats, setStats] = useState({ direct_count: 0, active_directs: 0 });
  const [cfg, setCfg] = useState(null);
  useEffect(() => {
    (async () => {
      const [{ data: s }, cfgResp] = await Promise.all([
        api.get("/team/stats"),
        api.get("/public/config").catch(() => ({ data: null })),
      ]);
      setStats(s); setCfg(cfgResp.data);
    })();
  }, []);
  const directPct = cfg?.direct_referral_bonus_pct ?? 5;
  const levelPcts = cfg?.level_income_pcts || [10, 5, 3, 2, 1];
  const level6Plus = cfg?.level_6plus_pct ?? 0.5;

  const link = `${window.location.origin}/register?ref=${user?.referral_code||""}`;
  const copy = async () => { await navigator.clipboard.writeText(link); toast.success("Link copied"); };
  const shareVia = (target) => {
    const text = encodeURIComponent(`Join me on Next Billion — AI Crypto Trading Network. Use my referral: ${link}`);
    const map = {
      whatsapp: `https://wa.me/?text=${text}`,
      telegram: `https://t.me/share/url?url=${encodeURIComponent(link)}&text=${text}`,
      twitter: `https://twitter.com/intent/tweet?text=${text}`,
    };
    window.open(map[target], "_blank");
  };

  return (
    <AppLayout variant="user">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-nb-cyan">Grow your network</div>
        <h1 className="font-heading font-bold text-3xl md:text-4xl text-white mt-1">Referrals</h1>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="nb-card p-6 lg:col-span-2">
          <div className="text-xs uppercase tracking-widest text-slate-400">Your Referral Link</div>
          <div className="mt-3 rounded-xl bg-[#070B14] border border-white/10 p-4 flex items-center gap-2">
            <div className="font-mono text-sm text-slate-200 break-all flex-1" data-testid="referral-link">{link}</div>
            <button onClick={copy} data-testid="btn-copy-referral" className="nb-btn-primary text-xs whitespace-nowrap"><Copy size={12} className="inline mr-1"/> Copy</button>
          </div>
          <div className="mt-3 text-xs text-slate-500">Code: <span className="font-mono text-nb-cyan">{user?.referral_code}</span></div>

          <div className="mt-6 grid grid-cols-3 gap-3">
            <button data-testid="share-whatsapp" onClick={()=>shareVia("whatsapp")} className="rounded-lg border border-white/10 py-3 text-sm text-slate-200 hover:border-[#8AE234]/40"><MessageCircle size={14} className="inline mr-1"/> WhatsApp</button>
            <button data-testid="share-telegram" onClick={()=>shareVia("telegram")} className="rounded-lg border border-white/10 py-3 text-sm text-slate-200 hover:border-[#8AE234]/40"><Share2 size={14} className="inline mr-1"/> Telegram</button>
            <button data-testid="share-twitter" onClick={()=>shareVia("twitter")} className="rounded-lg border border-white/10 py-3 text-sm text-slate-200 hover:border-[#8AE234]/40"><Share2 size={14} className="inline mr-1"/> X/Twitter</button>
          </div>

          <div className="mt-6 rounded-xl border border-white/10 p-4 text-xs text-slate-400">
            <strong className="text-white">Earn Structure:</strong> Direct referral bonus <span className="neon-text font-mono">{directPct}%</span> on referee's first investment. Plus multi-level team income up to Level 5: {levelPcts.map(p => `${p}%`).join(" / ")} of team's daily profit. Level 6+ pays <span className="font-mono text-nb-cyan">{level6Plus}%</span> unlimited depth.
          </div>
        </div>

        <div className="nb-card p-6">
          <div className="text-xs uppercase tracking-widest text-slate-400">Direct Members</div>
          <div className="mt-2 font-mono text-4xl neon-text">{stats.direct_count}</div>
          <div className="text-xs text-slate-500 mt-1">{stats.active_directs} active</div>
          <div className="mt-6 text-xs text-slate-400 flex items-center gap-2"><Users2 size={14}/> Total referral bonus earned: <span className="font-mono text-white">${(user?.total_referral_bonus||0).toFixed(2)}</span></div>
          <div className="mt-2 text-xs text-slate-400 flex items-center gap-2"><Users2 size={14}/> Total team income earned: <span className="font-mono text-white">${(user?.total_team_income||0).toFixed(2)}</span></div>
        </div>
      </div>
    </AppLayout>
  );
}
